import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Lock, Loader2, ShieldCheck, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const IDLE_CHOICES = [1, 3, 5, 10, 15, 30];

const SecuritySettings = () => {
  const { user, reloadIdleSettings } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = hasRole(user, 'super_admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [idle, setIdle] = useState(3);
  const [warn, setWarn] = useState(30);
  const [original, setOriginal] = useState({ idle: 3, warn: 30 });

  const load = useCallback(async () => {
    try {
      const r = await api.get('/settings/security');
      const m = Number(r.data?.idle_timeout_minutes) || 3;
      const w = Number(r.data?.idle_warning_seconds) || 30;
      setIdle(m); setWarn(w);
      setOriginal({ idle: m, warn: w });
    } catch (e) {
      toast({ title: 'Failed to load security settings', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const dirty = idle !== original.idle || warn !== original.warn;

  const save = async () => {
    if (!isSuperAdmin) return;
    setSaving(true);
    try {
      await api.put('/settings/security', { idle_timeout_minutes: idle, idle_warning_seconds: warn });
      toast({ title: 'Security settings updated', description: `Users will now auto-logout after ${idle} min idle.` });
      setOriginal({ idle, warn });
      reloadIdleSettings && reloadIdleSettings();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Could not save settings', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading security settings…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-50 text-amber-700 grid place-items-center"><Lock className="h-5 w-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900 text-base">Session timeout</h3>
              <p className="text-xs text-slate-500 mt-0.5">Auto-logout signed-in users after a period of inactivity. A warning toast appears shortly before the forced logout.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Idle timeout (minutes)</Label>
              <Select value={String(idle)} onValueChange={(v) => setIdle(Number(v))} disabled={!isSuperAdmin}>
                <SelectTrigger className="mt-1.5" data-testid="security-idle-timeout-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IDLE_CHOICES.map((m) => (
                    <SelectItem key={m} value={String(m)} data-testid={`security-idle-option-${m}`}>{m} minute{m > 1 ? 's' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Warning before logout (seconds)</Label>
              <Select value={String(warn)} onValueChange={(v) => setWarn(Number(v))} disabled={!isSuperAdmin}>
                <SelectTrigger className="mt-1.5" data-testid="security-warn-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 45, 60].map((s) => (
                    <SelectItem key={s} value={String(s)} data-testid={`security-warn-option-${s}`}>{s} s</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isSuperAdmin && (
            <div className="text-[11px] text-slate-500 italic">Only Super Admins can change these settings.</div>
          )}

          <div className="flex items-center justify-end pt-2">
            <Button
              onClick={save}
              disabled={!isSuperAdmin || !dirty || saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="security-save-btn"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center"><ShieldCheck className="h-5 w-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900 text-base">Password policy</h3>
              <p className="text-xs text-slate-500 mt-0.5">Enforced server-side on every user creation and password reset.</p>
              <ul className="text-xs text-slate-700 mt-2 space-y-1 list-disc list-inside">
                <li>Minimum length: <strong>10 characters</strong></li>
                <li>Must include at least <strong>3 of</strong>: lowercase, UPPERCASE, digits, symbols</li>
                <li>Brute-force protection: <strong>5 failed logins / 15 min</strong> per (IP + username)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
